import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { MemberSelect } from '../ui/UserSelect';
import {
  createTicket,
  listCategories,
  priorityMatrix,
  TICKET_IMPACTS,
  TICKET_KINDS,
  TICKET_URGENCIES,
  type TicketImpact,
  type TicketKind,
  type TicketUrgency,
} from './api';

/**
 * A new chamado.
 *
 * One required field: what the problem is. Everything else has a sensible default, because a ticket
 * is usually opened while somebody is on the phone, and a form that asks for a category, a queue and
 * a priority before it will accept "o boleto não gera" is a form that gets bypassed by an e-mail to
 * a colleague — which is how support work becomes invisible.
 *
 * The priority is not asked for. It is computed from urgency and impact, the way ITIL has it and the
 * way the server will compute it anyway: "quantos minutos isto vale" is a question nobody answers
 * consistently, while "quão rápido isto precisa" and "quantas pessoas isto atinge" are questions the
 * person on the phone can actually answer. The result is shown under the two selects, so the promise
 * being taken on is visible before the chamado is opened.
 */
export function NewTicketDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const client = useQueryClient();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<TicketUrgency>('MEDIUM');
  const [impact, setImpact] = useState<TicketImpact>('LOW');
  const [kind, setKind] = useState<TicketKind>('INCIDENT');
  const [categoryId, setCategoryId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const categories = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: listCategories,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  useEffect(() => {
    const element = dialog.current;

    if (!element) return;

    if (open && !element.open) {
      setSubject('');
      setDescription('');
      setUrgency('MEDIUM');
      setImpact('LOW');
      setKind('INCIDENT');
      setCategoryId('');
      setAssigneeId('');
      setRequesterName('');
      setRequesterEmail('');
      setError(null);
      element.showModal();
    }

    if (!open && element.open) element.close();
  }, [open]);

  /*
   * The matrix comes from the server, so this preview and the stored priority cannot disagree.
   * While it is loading the line shows a dash rather than a guess.
   */
  const matrix = useQuery({
    queryKey: ['priority-matrix'],
    queryFn: priorityMatrix,
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const derived = matrix.data?.matrix[urgency]?.[impact] ?? null;

  const create = useMutation({
    mutationFn: () =>
      createTicket({
        subject: subject.trim(),
        urgency,
        impact,
        kind,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(requesterName.trim() ? { requesterName: requesterName.trim() } : {}),
        ...(requesterEmail.trim() ? { requesterEmail: requesterEmail.trim() } : {}),
      }),
    onSuccess: (ticket) => {
      void client.invalidateQueries({ queryKey: ['tickets'] });
      void client.invalidateQueries({ queryKey: ['ticket-summary'] });
      onClose();
      navigate(`/atendimento/chamados/${ticket.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!open) return null;

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (subject.trim()) create.mutate();
        }}
      >
        <h2>{t('support.newTicket')}</h2>
        <p className="muted">{t('support.newTicketHint')}</p>

        <label className="stack__field">
          <span>
            {t('support.subject')} <span className="required">*</span>
          </span>
          <input
            value={subject}
            placeholder={t('support.subjectPlaceholder')}
            maxLength={300}
            autoFocus
            onChange={(event) => setSubject(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('support.body')}</span>
          <textarea
            rows={4}
            value={description}
            maxLength={20_000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('support.category')}</span>
            <select
              className="select"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">{t('support.noCategory')}</option>
              {(categories.data?.categories ?? [])
                .filter((category) => category.active)
                .map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
            </select>
          </label>

          <label className="stack__field">
            <span>{t('support.kind')}</span>
            <select
              className="select"
              value={kind}
              onChange={(event) => setKind(event.target.value as TicketKind)}
            >
              {TICKET_KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(`support.kind.${value}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('support.urgency')}</span>
            <select
              className="select"
              value={urgency}
              onChange={(event) => setUrgency(event.target.value as TicketUrgency)}
            >
              {TICKET_URGENCIES.map((value) => (
                <option key={value} value={value}>
                  {t(`support.urgency.${value}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="stack__field">
            <span>{t('support.impact')}</span>
            <select
              className="select"
              value={impact}
              onChange={(event) => setImpact(event.target.value as TicketImpact)}
            >
              {TICKET_IMPACTS.map((value) => (
                <option key={value} value={value}>
                  {t(`support.impact.${value}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="muted small">
          {t('support.priorityWillBe').replace(
            '{value}',
            derived === null ? '—' : t(`support.priority.${derived}`),
          )}
        </p>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('support.requester')}</span>
            <input
              value={requesterName}
              maxLength={200}
              onChange={(event) => setRequesterName(event.target.value)}
            />
          </label>

          <label className="stack__field">
            <span>{t('crm.field.email')}</span>
            <input
              type="email"
              value={requesterEmail}
              maxLength={254}
              onChange={(event) => setRequesterEmail(event.target.value)}
            />
          </label>
        </div>

        <div className="stack__field">
          <span>{t('support.assignee')}</span>
          {/* Empty means the queue decides, which is the right default for a team with a rota. */}
          <MemberSelect
            value={assigneeId || null}
            placeholder={t('support.byQueue')}
            onChange={(next) => setAssigneeId(next ?? '')}
          />
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={subject.trim() === '' || create.isPending}
          >
            {create.isPending ? t('work.creating') : t('support.openTicket')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
